import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { sectionRenderer, morphSection } from '@theme/section-renderer';
import { StandardEvents } from '@shopify/events';
import { DrawerCloseEvent, DrawerOpenEvent } from '@theme/theme-drawer';
import { swr, themeScope } from '@theme/custom-swr';
import { backStack } from '@theme/custom-history';

/**
 * Phone bottom navigation (snippets/custom-tab-bar.liquid, README §11.12).
 *
 * Speed rule: a tap changes the screen within ONE frame (< 16.7 ms to the
 * next paint). Every tap handler only flips attributes on markup that is
 * already in the page; network work happens before the tap (prefetch) or
 * after the paint (revalidate). Each tap is measured as a `q-tab:<name>`
 * performance entry (tap → next frame), visible in DevTools › Performance.
 *
 * Data per tab (big-store pattern: Amazon / Noon apps, React Query):
 * - Categories: stale-while-revalidate through assets/custom-swr.js. Loaded
 *   when the browser is idle (or on touch), painted into the hidden screen
 *   ahead of time, so opening it is an un-hide. Fresh for 5 min; after that
 *   the cached screen shows at once and refreshes in the background. Kept
 *   for the session, keyed by theme, language, country and the category
 *   list itself — a change of any of them is a different key.
 * - Cart: never cached. The drawer opens at once with what the page has and
 *   is re-rendered from the server on every open.
 * - Account: an in-app screen drawn by the server with the page (sign in,
 *   orders, addresses, wishlist, help) — no network, so it opens like
 *   Categories. Only its links leave for Shopify's account pages.
 * - Home: a real page. In the tap's frame its skeleton covers the old page
 *   (tab and screen always match, no progress bar); the page replaces it when
 *   it arrives. Prefetched on touch (speculation rules in custom-globals).
 * - Any link to a product or a listing (home cards, category tiles…): its
 *   skeleton appears under the real header in the same frame (#onNavigate),
 *   so the shopper lands on the target screen at once and watches it fill.
 *
 * Fast switching (Home → Categories → Home → Categories within ms): the UI
 * follows the LAST tap synchronously; the one categories request is shared
 * (never duplicated, never cancelled — its answer is still the freshest
 * data), and the back-button history entry is reconciled when the browser
 * catches up.
 */

const CATEGORIES_CACHE = { staleTime: 5 * 60 * 1000, maxAge: 24 * 60 * 60 * 1000, persist: true };
const SECTION_ID = 'shopify-section-custom-tab-categories';
const CART_SECTION_ID = 'cart-drawer-section';
const KEYBOARD_RATIO = 0.75;
/** The tab screen's step in the shared back stack */
const SCREEN_KEY = 'q-tab-screen';
/** My orders, a sub-screen of Account (its own Back step) */
const ORDERS_KEY = 'q-orders';
/** Screens that live in this page (Back closes them); the others stand in for a page on its way */
const IN_PAGE_VIEWS = new Set(['categories', 'account']);
/** Page skeletons drawn under the real header, which stays identical across the page change */
const BELOW_HEADER_VIEWS = new Set(['product', 'listing']);
/** Where the bar is shown (custom-tab-bar.css) */
const PHONE = window.matchMedia('screen and (max-width: 749px)');

/**
 * @typedef {Object} TabBarRefs
 * @property {HTMLElement} bar
 * @property {HTMLElement} screen
 * @property {HTMLElement} categoriesBody
 * @property {HTMLElement} categoriesError
 * @property {HTMLAnchorElement[]} tabs
 */

/** @extends {Component<TabBarRefs>} */
class TabBar extends Component {
  requiredRefs = ['bar', 'screen', 'categoriesBody', 'categoriesError', 'tabs'];

  /** @type {'categories' | 'account' | 'home' | 'product' | 'listing' | null} what the screen shows; null = closed */
  #view = null;
  /** The categories markup currently painted (to skip identical repaints) */
  #painted = '';
  /** @type {string | null} the tab whose page is on its way (a page change not arrived yet) */
  #leaving = null;
  /** Bumped by every cart change, so a slower background refresh never overwrites a newer cart */
  #cartRevision = 0;
  /** @type {() => void} */
  #unsubscribe = () => {};

  get #cacheKey() {
    // v3: Flipkart rail + panes (2026-10-15) — bump when the markup's shape changes
    return `${themeScope()}tab-categories:v3:${this.dataset.scope ?? ''}`;
  }

  get #cacheOptions() {
    const url = this.dataset.categoriesUrl ?? '/?section_id=custom-tab-categories';
    return {
      ...CATEGORIES_CACHE,
      /** @param {AbortSignal} signal */
      fetcher: async (signal) => {
        const response = await fetch(url, { signal });
        if (!response.ok) throw new Error(`Categories: HTTP ${response.status}`);
        return response.text();
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();

    this.#unsubscribe = swr.subscribe(this.#cacheKey, this.#cacheOptions, (html) => this.#paintCategories(html));
    window.addEventListener('pageshow', this.#onPageShow);
    window.addEventListener('pagehide', this.#onPageHide);
    window.addEventListener('pageswap', this.#onPageSwap);
    navigationApi()?.addEventListener('navigate', this.#onNavigate);
    if (!navigationApi()) document.addEventListener('click', this.#onLinkClick);
    document.addEventListener('click', this.#onOpenTabClick, true);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartChange);
    document.addEventListener(DrawerCloseEvent.eventName, this.#onDrawerClose);
    document.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerOpen);
    window.visualViewport?.addEventListener('resize', this.#onViewportResize);
    document.addEventListener('focusin', this.#onFocusIn);

    this.#restoreFromHistory();

    // Paint what the session already has into the parked screen, and warm the
    // cache, while nothing else is happening — so the first tap is an un-hide.
    requestIdleCallback(
      () => {
        this.#prerender(); // the skeleton, in case the first tap beats the data
        this.#prepareCategories();
      },
      { timeout: 3000 }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unsubscribe();
    window.removeEventListener('pageshow', this.#onPageShow);
    window.removeEventListener('pagehide', this.#onPageHide);
    window.removeEventListener('pageswap', this.#onPageSwap);
    navigationApi()?.removeEventListener('navigate', this.#onNavigate);
    document.removeEventListener('click', this.#onLinkClick);
    document.removeEventListener('click', this.#onOpenTabClick, true);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartChange);
    document.removeEventListener(DrawerCloseEvent.eventName, this.#onDrawerClose);
    document.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerOpen);
    document.removeEventListener('click', this.#onClickOverDrawer, true);
    window.visualViewport?.removeEventListener('resize', this.#onViewportResize);
    document.removeEventListener('focusin', this.#onFocusIn);
  }

  /** Keyboard focus never wanders onto the page covered by an open screen */
  #onFocusIn = (/** @type {FocusEvent} */ event) => {
    if (!this.#view || !(event.target instanceof Element) || !event.target.closest('.page-wrapper')) return;
    const title = /** @type {HTMLElement | null} */ (
      this.refs.screen.querySelector(`[data-for="${this.#view}"] .q-tabscreen__title`)
    );
    (title ?? this.refs.screen).focus({ preventScroll: true });
  };

  /**
   * Finger down on a tab: start loading before the tap completes (~100 ms head start).
   * @param {PointerEvent} event
   */
  warmTab(event) {
    const tab = /** @type {HTMLElement} */ (event.target).closest('[data-tab]');
    if (tab instanceof HTMLElement && tab.dataset.tab === 'categories') this.#loadCategories();
  }

  /**
   * A tab was tapped. Runs before the browser follows the link.
   * @param {MouseEvent} event
   */
  selectTab(event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    const tab = /** @type {HTMLElement} */ (event.target).closest('[data-tab]');
    if (!(tab instanceof HTMLAnchorElement)) return;

    this.#activate(tab, event);
    measureTap(tab.dataset.tab ?? 'tab', event.timeStamp);
  }

  /**
   * What a tab does. With `event`, a page tab lets the browser follow its link;
   * without one (tap caught over the open cart drawer), it navigates itself.
   * @param {HTMLAnchorElement} tab
   * @param {MouseEvent | null} event
   */
  #activate(tab, event) {
    const name = tab.dataset.tab;
    const prevent = () => event?.preventDefault();
    const follow = () => {
      if (!event) location.assign(tab.href);
    };

    // The last tap wins. A page still on its way is dropped for an in-page tab;
    // another page tab simply replaces it (the browser cancels the first).
    if (this.#leaving === name) {
      prevent(); // already going there
      return;
    }
    if (this.#leaving && (name !== 'home' || this.hasAttribute('data-on-home'))) {
      this.#cancelLeaving();
    }

    switch (name) {
      case 'categories':
      case 'account':
        // Always the in-app screen. (An exception that followed the /account link when
        // Account was the current tab sent the Wishlist page — which highlights Account —
        // to Shopify's sign-in, a 404 on the local preview. Fixed 2026-10-15.)
        prevent();
        if (name === 'account' && this.refs.screen.dataset.view === 'orders') {
          backStack.closed(ORDERS_KEY);
          this.#hideOrders();
        } else if (this.#view === name) {
          this.refs.screen.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          this.#openScreen(name);
        }
        this.#setActive(name);
        break;

      case 'home':
        if (this.hasAttribute('data-on-home')) {
          prevent();
          if (this.#view) {
            this.#closeScreen();
          } else {
            scrollPageToTop();
          }
          this.#setActive('home');
        } else {
          this.#goToPage('home', follow);
        }
        break;

      case 'cart': {
        const drawer = cartDrawer();
        if (tab.hasAttribute('data-drawer') && drawer?.open) {
          prevent();
          // Highlight and drawer appear together. Both run one task after the tap:
          // Horizon's open (scroll lock, showModal, focus) restyles the page (~20 ms)
          // and must not hold the tap's own frame.
          afterNextPaint(() => {
            this.#setActive('cart');
            drawer.open?.();
            this.#refreshCart();
          });
        } else if (this.dataset.current === 'cart') {
          prevent(); // already on the cart page
          scrollPageToTop();
        } else {
          follow();
        }
        break;
      }
    }
  }

  /**
   * A tab that is another page: in the tap's frame the tab is highlighted AND
   * its skeleton screen covers the old page, so tab and screen always match;
   * the real page replaces it when it arrives (Horizon's page transition
   * cross-fades skeleton → page). No progress bar for tab changes
   * (assets/custom-loading.js skips `html[data-q-tab-nav]`).
   * @param {'home'} name
   * @param {() => void} follow - navigates when the browser will not (no click event)
   */
  #goToPage(name, follow) {
    this.#leaving = name;
    document.documentElement.toggleAttribute('data-q-tab-nav', true);
    this.#openScreen(name);
    this.#setActive(name);
    follow();
  }

  /* ---- Any link to a product or a listing ------------------------------ */

  /**
   * Every page change in this document (links, Horizon's product cards that
   * navigate from script, the category tiles) passes here first. For a product
   * or a listing, its skeleton is shown under the real header in this very
   * frame, and the top progress bar stays off — the shopper lands on the
   * target screen at once and sees it fill, instead of watching the old
   * screen with a loading bar.
   * @param {any} event - NavigateEvent
   */
  #onNavigate = (event) => {
    if (event.navigationType !== 'push' && event.navigationType !== 'replace') return;
    if (event.hashChange || event.downloadRequest || event.formData || event.destination.sameDocument) return;
    this.#showSkeletonFor(new URL(event.destination.url));
  };

  /**
   * A link marked data-q-open-tab (the Home row's "All" tile) opens that tab's
   * screen on phones instead of a page — same screen, same highlight as the tab.
   * @param {MouseEvent} event
   */
  #onOpenTabClick = (event) => {
    if (!PHONE.matches || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey) return;
    const link = event.target instanceof Element ? event.target.closest('a[data-q-open-tab]') : null;
    const tab = link && this.refs.tabs.find((candidate) => candidate.dataset.tab === link.getAttribute('data-q-open-tab'));
    if (!tab) return;
    event.preventDefault();
    this.#activate(tab, null);
    measureTap(tab.dataset.tab ?? 'tab', event.timeStamp);
  };

  /** Same, for browsers without the Navigation API (plain link taps) */
  #onLinkClick = (/** @type {MouseEvent} */ event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!(link instanceof HTMLAnchorElement) || link.target === '_blank' || link.hasAttribute('download')) return;
    if (link.closest('q-tab-bar .q-tabbar__bar')) return; // tabs handle themselves
    this.#showSkeletonFor(new URL(link.href));
  };

  /** @param {URL} url */
  #showSkeletonFor(url) {
    if (!PHONE.matches || this.#leaving) return; // a tab already shows its screen
    if (url.origin !== location.origin) return;
    if (url.pathname === location.pathname && url.search === location.search) return;

    const view = skeletonFor(url);
    if (!view) return;

    this.#leaving = view;
    document.documentElement.toggleAttribute('data-q-tab-nav', true);
    this.#openScreen(view);
    if (view === 'home') this.#setActive('home');
    if (view === 'listing') this.#setActive(isWishlist(url) ? 'account' : 'categories');
  }

  /* ---- Bar above the open cart drawer ---------------------------------- */

  /**
   * The cart drawer is a modal <dialog>: the browser puts it and its backdrop
   * in the top layer, above everything, so the bar vanished while the cart was
   * open. On phones the bar now joins the top layer above it (a manual
   * popover) and the drawer ends where the bar begins (custom-tab-bar.css,
   * html[data-q-tabbar-raised]) — the bar stays in place, as in native apps.
   * @param {Event} event
   */
  #onDrawerOpen = (event) => {
    if (!(event.target instanceof Element) || event.target.id !== 'cart-drawer' || !PHONE.matches) return;
    const { bar } = this.refs;
    const dialog = event.target.querySelector('dialog');

    document.documentElement.toggleAttribute('data-q-tabbar-raised', true);
    if (!bar.matches(':popover-open')) {
      bar.popover = 'manual';
      bar.showPopover();
    }
    this.#setActive('cart');
    document.addEventListener('click', this.#onClickOverDrawer, true);
    // Lowered once the dialog has really closed (after its slide-out), so the drawer never jumps
    dialog?.addEventListener('close', this.#lowerBar, { once: true });
  };

  #lowerBar = () => {
    const { bar } = this.refs;
    document.removeEventListener('click', this.#onClickOverDrawer, true);
    if (bar.matches(':popover-open')) bar.hidePopover();
    bar.removeAttribute('popover');
    document.documentElement.removeAttribute('data-q-tabbar-raised');
    this.#setActive(this.#view ?? this.dataset.current ?? '');
  };

  /**
   * Content above a modal dialog cannot be tapped (the browser makes it inert),
   * so a tap on the raised bar reaches the drawer's backdrop. It is caught here,
   * before Horizon's backdrop handler: the drawer closes and the tab runs.
   * @param {MouseEvent} event
   */
  #onClickOverDrawer = (event) => {
    const barBox = this.refs.bar.getBoundingClientRect();
    if (event.clientY < barBox.top || event.clientY > barBox.bottom) return;

    const tab = this.refs.tabs.find((candidate) => {
      const box = candidate.getBoundingClientRect();
      return event.clientX >= box.left && event.clientX <= box.right;
    });
    if (!tab) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    cartDrawer()?.close?.();
    if (tab.dataset.tab !== 'cart') this.#activate(tab, null); // Cart again = just close it
    measureTap(tab.dataset.tab ?? 'tab', event.timeStamp);
  };

  /**
   * The page was (re)loaded on a history step of one of our screens — Back from
   * Shopify's account pages without the back-forward cache. Reopen it, so the
   * screen matches the history and the next Back closes it as expected.
   */
  #restoreFromHistory() {
    const { qScreen, qSub, qBack } = backStack.state;
    if (!qBack || !(typeof qScreen === 'string' && IN_PAGE_VIEWS.has(qScreen))) return;
    this.#openScreen(/** @type {'categories' | 'account'} */ (qScreen), { restore: true });
    this.#setActive(qScreen);
    if (qSub === 'orders' && qScreen === 'account') this.#showOrders({ restore: true });
  }

  /** "My orders" on the Account screen (signed in): the in-app orders list */
  openOrders() {
    this.#showOrders();
  }

  /** The ← of a sub-screen (My orders): same as Back */
  screenBack() {
    if (backStack.has(ORDERS_KEY)) history.back();
    else this.#hideOrders();
  }

  /** @param {{ restore?: boolean }} [options] */
  #showOrders({ restore = false } = {}) {
    const { screen } = this.refs;
    screen.dataset.view = 'orders';
    screen.scrollTo({ top: 0 });
    if (restore) {
      backStack.adopt(ORDERS_KEY, () => this.#hideOrders());
    } else {
      afterNextPaint(async () => {
        await backStack.settled();
        if (screen.dataset.view !== 'orders') return;
        backStack.open(ORDERS_KEY, () => this.#hideOrders(), { qScreen: 'account', qSub: 'orders' });
      });
    }
    afterNextPaint(() => {
      /** @type {HTMLElement | null} */ (screen.querySelector('[data-for="orders"] .q-tabscreen__title'))?.focus({ preventScroll: true });
    });
  }

  #hideOrders() {
    if (this.refs.screen.dataset.view !== 'orders') return;
    this.refs.screen.dataset.view = 'account';
  }

  /** Stops a page change a tab started (the shopper tapped another tab before it arrived) */
  #cancelLeaving() {
    this.#leaving = null;
    document.documentElement.removeAttribute('data-q-tab-nav');
    window.stop();
    if (this.#view && !IN_PAGE_VIEWS.has(this.#view)) this.#closeScreen();
  }

  /** @type {string} the department chosen in the Categories rail (kept across refreshes) */
  #department = '';

  /**
   * A department in the Categories rail: show its pane, in the page (no request).
   * @param {MouseEvent} event
   */
  selectDepartment(event) {
    const button = /** @type {HTMLElement} */ (event.target).closest('[data-pane]');
    if (!(button instanceof HTMLElement)) return;
    this.#department = button.dataset.pane ?? '';
    this.#showDepartment(this.#department);
  }

  /** @param {string} paneId */
  #showDepartment(paneId) {
    const body = this.refs.categoriesBody;
    const pane = paneId ? body.querySelector(`#${CSS.escape(paneId)}`) : null;
    if (!pane) return;
    for (const tab of body.querySelectorAll('.q-dept[data-pane]')) {
      const selected = tab.getAttribute('data-pane') === paneId;
      tab.setAttribute('aria-selected', String(selected));
      tab.setAttribute('tabindex', selected ? '0' : '-1');
    }
    for (const other of body.querySelectorAll('.q-dept-pane')) {
      /** @type {HTMLElement} */ (other).hidden = other !== pane;
    }
    body.querySelector('.q-fk__panes')?.scrollTo({ top: 0 });
  }

  /** The error state's "Try again" button */
  retryCategories() {
    this.refs.categoriesError.hidden = true;
    this.refs.categoriesBody.hidden = false;
    this.#loadCategories();
  }

  /* ---- Screen ---------------------------------------------------------- */

  /**
   * Inside the tap: un-park the screen (a root-only change, see #prerender);
   * the page under it stays rendered (setPageBehind). Everything else —
   * focus (forces layout), history, revalidation — runs right after that
   * frame is on screen. Product / listing skeletons start under the header,
   * which stays on screen.
   * @param {'categories' | 'account' | 'home' | 'product' | 'listing'} view
   * @param {{ restore?: boolean }} [options] - reopened for an existing history step (no new step)
   */
  #openScreen(view, { restore = false } = {}) {
    const { screen } = this.refs;
    const wasOpen = this.#view !== null;
    const belowHeader = BELOW_HEADER_VIEWS.has(view);

    // Cached but not painted yet (tap before the idle paint): paint it in this frame, never a skeleton flash
    if (view === 'categories' && !this.#painted) {
      const cached = swr.peek(this.#cacheKey, CATEGORIES_CACHE);
      if (cached) this.#paintCategories(cached.value);
    }

    if (belowHeader) {
      // The skeleton's content starts where the next page's will: under the whole
      // header group (utility bar + app bar, the same height on every phone page),
      // even when the header is scrolled away now — so nothing moves when it lands.
      const header = document.querySelector('#header-component');
      const top = Math.max(0, header?.getBoundingClientRect().bottom ?? 0);
      let group = 0;
      for (const section of document.querySelectorAll('.shopify-section-group-header-group')) {
        group += /** @type {HTMLElement} */ (section).offsetHeight;
      }
      screen.style.setProperty('--q-screen-top', `${top}px`);
      screen.style.setProperty('--q-screen-pad', `${Math.max(0, group - top)}px`);
    }
    if (screen.dataset.view !== view) screen.dataset.view = view;
    if (wasOpen && IN_PAGE_VIEWS.has(view) && backStack.has(SCREEN_KEY)) {
      if (backStack.has(ORDERS_KEY)) backStack.closed(ORDERS_KEY); // leaving My orders for another tab screen
      else history.replaceState({ ...backStack.state, qScreen: view, qSub: null }, ''); // the step now reopens this screen
    }
    screen.toggleAttribute('data-open', true);
    setPageBehind(!belowHeader);
    this.#view = view;
    // Reopened for a step already in the history (page reloaded on it): adopt it now,
    // before any sub-screen (My orders) adopts the step above it
    if (restore && IN_PAGE_VIEWS.has(view)) backStack.adopt(SCREEN_KEY, () => this.#closeScreen({ fromHistory: true }));

    afterNextPaint(async () => {
      if (this.#view !== view) return; // another tap already changed it
      if (!IN_PAGE_VIEWS.has(view)) return;

      // One Back step for the screen (the shared back stack, assets/custom-history.js).
      // Wait for any step being removed (e.g. the cart drawer this tap just closed).
      if (!restore) {
        await backStack.settled();
        if (this.#view !== view) return;
        backStack.open(SCREEN_KEY, () => this.#closeScreen({ fromHistory: true }), { qScreen: view, qSub: null });
      }
      if (!wasOpen) {
        /** @type {HTMLElement | null} */ (screen.querySelector(`[data-for="${view}"] .q-tabscreen__title`))?.focus({
          preventScroll: true,
        });
      }
      if (view === 'categories') this.#loadCategories();
    });
  }

  #closeScreen({ fromHistory = false } = {}) {
    if (!this.#view) return;

    this.refs.screen.removeAttribute('data-open');
    setPageBehind(false);
    this.#view = null;
    this.#setActive(this.#shownTab());
    if (fromHistory) {
      backStack.closed(ORDERS_KEY); // its step went with the screen's
      return; // Back already removed the step
    }

    afterNextPaint(() => {
      if (this.#view) return; // re-opened in the meantime: keep the step
      backStack.closed(SCREEN_KEY, ORDERS_KEY);
    });
  }

  /**
   * Leaving for a page that has now arrived. If the browser keeps this page for
   * Back (bfcache), put it back the way it was BEFORE it is stored, so Back
   * shows the page itself — never the skeleton that stood in for the next one.
   */
  #onPageHide = (/** @type {PageTransitionEvent} */ event) => {
    if (event.persisted) this.#resetLeaving();
  };

  /**
   * Leaving with a skeleton on screen: no page transition. The skeleton IS the
   * transition; Horizon's cross-fade on top of it brought the covered page back
   * for a moment (its snapshot of <main> is taken under the skeleton) and slid
   * the new page in — the flicker and "shaking" photo on product taps
   * (measured 2026-10-15: old page visible again 380–500 ms after the tap).
   * Now the skeleton stays until the page is ready, then the page replaces it.
   * @param {Event & { viewTransition?: ViewTransition | null }} event - PageSwapEvent
   */
  #onPageSwap = (event) => {
    if (document.documentElement.hasAttribute('data-q-tab-nav')) event.viewTransition?.skipTransition();
  };

  /** Back to a page from the back/forward cache: drop any half-finished tap */
  #onPageShow = (/** @type {PageTransitionEvent} */ event) => {
    if (event.persisted) this.#resetLeaving();
  };

  #resetLeaving() {
    this.#leaving = null;
    document.documentElement.removeAttribute('data-q-tab-nav');
    if (this.#view && !IN_PAGE_VIEWS.has(this.#view)) this.#closeScreen();
    this.#setActive(this.#shownTab());
  }

  /**
   * The tab for what is on screen now: the open cart drawer first (it covers
   * everything — a page restored from the back/forward cache can come back
   * with it still open: Cart, not the page's own tab, fixed 2026-10-15), then
   * the open screen, then the page.
   */
  #shownTab() {
    if (cartDrawer()?.querySelector('dialog[open]')) return 'cart';
    return this.#view ?? this.dataset.current ?? '';
  }

  /** @param {string} name */
  #setActive(name) {
    for (const tab of this.refs.tabs) {
      if (tab.dataset.tab === name) {
        tab.setAttribute('aria-current', 'page');
      } else {
        tab.removeAttribute('aria-current');
      }
    }
  }

  /* ---- Categories data (stale-while-revalidate) ------------------------ */

  #prepareCategories() {
    const cached = swr.peek(this.#cacheKey, CATEGORIES_CACHE);
    if (cached) this.#paintCategories(cached.value);
    // @ts-ignore — Network Information API (Chromium)
    if (navigator.connection?.saveData && !cached) return;
    if (!cached || cached.isStale) this.#loadCategories();
  }

  #loadCategories() {
    const cached = swr.peek(this.#cacheKey, CATEGORIES_CACHE);
    if (cached && !this.#painted) this.#paintCategories(cached.value);

    swr
      .get(this.#cacheKey, this.#cacheOptions)
      .then((html) => this.#paintCategories(html))
      .catch((error) => {
        if (this.#painted) return; // keep showing the cached screen
        console.warn('[tab-bar]', error);
        if (this.#view === 'categories') {
          this.refs.categoriesBody.hidden = true;
          this.refs.categoriesError.hidden = false;
        }
      });
  }

  /** @param {string} html - Section Rendering API response */
  #paintCategories(html) {
    if (html === this.#painted) return;

    const next = new DOMParser().parseFromString(html, 'text/html').getElementById(SECTION_ID);
    if (!next) return;

    const body = this.refs.categoriesBody;
    if (this.#painted) {
      // Already showing data: Horizon's morph changes only what changed (keeps scroll and focus)
      morph(body, next, MORPH_OPTIONS);
    } else {
      body.replaceChildren(...next.childNodes);
    }

    this.#painted = html;
    if (this.#department) this.#showDepartment(this.#department); // a refresh keeps the shopper's choice
    body.setAttribute('aria-busy', 'false');
    body.hidden = false;
    this.refs.categoriesError.hidden = true;
    this.#prerender();
  }

  /**
   * Renders the parked (closed) screen once while idle, so its style and
   * layout already exist when it opens. Closed, it is `content-visibility:
   * hidden` and moved off-screen (custom-tab-bar.css), which KEEPS that
   * rendering state; opening is then a root-only change instead of styling,
   * laying out and painting ~230 elements inside the tap frame.
   */
  #prerender() {
    const { screen } = this.refs;
    if (this.#view || screen.hasAttribute('data-prerender')) return;
    screen.toggleAttribute('data-prerender', true);
    requestAnimationFrame(() => requestAnimationFrame(() => screen.removeAttribute('data-prerender')));
  }

  /* ---- Cart (never cached) --------------------------------------------- */

  #onCartChange = () => {
    this.#cartRevision += 1;
  };

  /** Cart drawer closed: the highlight goes back to the screen or page underneath */
  #onDrawerClose = (/** @type {Event} */ event) => {
    if (!(event.target instanceof Element) || event.target.id !== 'cart-drawer') return;
    if (document.documentElement.hasAttribute('data-q-tabbar-raised')) return; // #lowerBar does it after the slide-out
    this.#setActive(this.#view ?? this.dataset.current ?? '');
  };

  /** Re-render the open drawer from the server; skip if the cart changed meanwhile */
  async #refreshCart() {
    const revision = this.#cartRevision;
    try {
      const html = await sectionRenderer.getSectionHTML(CART_SECTION_ID, false);
      if (revision !== this.#cartRevision) return; // a newer cart is already on screen
      await morphSection(CART_SECTION_ID, html);
      syncCartBubbles();
    } catch (error) {
      console.warn('[tab-bar] cart refresh', error);
    }
  }

  /* ---- On-screen keyboard ---------------------------------------------- */

  /** The bar would ride up on top of the keyboard on Android: hide it while typing */
  #onViewportResize = () => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    const keyboardOpen = viewport.height < window.innerHeight * KEYBOARD_RATIO;
    document.documentElement.toggleAttribute('data-q-keyboard', keyboardOpen);
  };
}

/**
 * Runs `callback` once the current frame has been painted (rAF → next task),
 * so work that is not needed for that frame never delays it.
 * @param {() => void} callback
 */
function afterNextPaint(callback) {
  requestAnimationFrame(() => setTimeout(callback, 0));
}

/**
 * The page under an open screen stays RENDERED (re-measured 2026-10-15 at 4×
 * CPU: skipping it with content-visibility made coming back cost 64–73ms of
 * re-rendering on the grown home page; left rendered: 22–27ms, open 41–55ms).
 * It is only taken out of the accessibility tree (aria-hidden, after the
 * frame: no restyle) and keyboard focus is kept on the screen by #onFocusIn.
 * @param {boolean} behind
 */
function setPageBehind(behind) {
  afterNextPaint(() => {
    const page = document.querySelector('.page-wrapper');
    if (!page) return;
    const stillBehind = document.querySelector('.q-tabscreen[data-open]') !== null;
    if (behind && stillBehind) page.setAttribute('aria-hidden', 'true');
    else if (!stillBehind) page.removeAttribute('aria-hidden');
  });
}

function scrollPageToTop() {
  const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  window.scrollTo({ top: 0, behavior: smooth ? 'smooth' : 'auto' });
}

/** @returns {any} the Navigation API (Chromium), or undefined */
function navigationApi() {
  return /** @type {any} */ (window).navigation;
}

/**
 * Which page skeleton stands in for a URL of this store, if any.
 * Locale prefixes (/ar/…) are allowed; cart, checkout, account and apps get none.
 * @param {URL} url
 * @returns {'home' | 'product' | 'listing' | null}
 */
function skeletonFor(url) {
  const path = url.pathname.replace(/^\/[a-z]{2}(-[a-z]{2})?(?=\/|$)/i, '') || '/';
  if (/\/products\/[^/]+\/?$/.test(path)) return 'product';
  if (/^\/(collections(\/[^/]+)?|search)\/?$/.test(path)) return 'listing';
  if (path === '/') return 'home';
  return null;
}

/**
 * The wishlist (snippets/custom-wishlist-url.liquid) belongs to the Account tab.
 * @param {URL} url
 */
function isWishlist(url) {
  return url.searchParams.get('view') === 'wishlist' || /\/pages\/wishlist\/?$/.test(url.pathname);
}

/** @returns {(HTMLElement & { open?: () => void, close?: () => void }) | null} Horizon's cart <theme-drawer> */
function cartDrawer() {
  return document.getElementById('cart-drawer');
}

/** Copies the count from the freshly rendered drawer to every cart bubble */
function syncCartBubbles() {
  const badge = document.querySelector('#cart-drawer .theme-drawer__badge');
  const count = Number.parseInt(badge?.textContent ?? '0', 10) || 0;
  for (const icon of document.querySelectorAll('cart-icon')) {
    const cartIcon = /** @type {HTMLElement & { currentCartCount?: number, renderCartBubble?: Function }} */ (icon);
    if (cartIcon.currentCartCount !== count) cartIcon.renderCartBubble?.(count, false);
  }
}

/**
 * Records tap → next frame as a performance entry `q-tab:<name>`.
 * @param {string} name
 * @param {number} start - event.timeStamp
 */
function measureTap(name, start) {
  requestAnimationFrame(() => {
    try {
      performance.measure(`q-tab:${name}`, { start, end: performance.now() });
    } catch {
      // Older browsers without the options form: nothing to record
    }
  });
}

if (!customElements.get('q-tab-bar')) {
  customElements.define('q-tab-bar', TabBar);
}
